<script setup lang="ts">
export interface BaseHeaderLinksProps {
  links: IHeaderLink[];
  leftOffset?: number;
  rightOffset?: number;
}

const props = withDefaults(defineProps<BaseHeaderLinksProps>(), {
  leftOffset: 0,
  rightOffset: 0,
});

const visibleLinks = ref<IHeaderLink[]>([]);
const invisibleLinks = ref<IHeaderLink[]>([]);

const linkWidthCache = ref(new Map<string, number>());

const measureTextWidth = (text: string): number => {
  const span = document.createElement("span");
  span.style.visibility = "hidden";
  span.style.position = "absolute";
  span.style.fontWeight = "500";
  span.style.whiteSpace = "nowrap";
  span.textContent = text;
  document.body.appendChild(span);
  const width = span.offsetWidth;
  document.body.removeChild(span);
  return width;
};

const getLinkWidth = (title: string): number => {
  if (linkWidthCache.value.has(title))
    return linkWidthCache.value.get(title)!;

  const width = Math.min(measureTextWidth(title), 144) + 48;
  linkWidthCache.value.set(title, width);
  return width;
};

const calculateVisibleLinks = () => {
  if (!props.links.length || !import.meta.client)
    return;

  let totalWidth = 0;
  const maxWidth = window.innerWidth - props.rightOffset - props.leftOffset - 100;

  const newVisibleLinks: IHeaderLink[] = [];
  const newInvisibleLinks: IHeaderLink[] = [];

  for (const link of props.links) {
    const linkWidth = getLinkWidth(link.title);
    totalWidth += linkWidth;

    if (totalWidth <= maxWidth)
      newVisibleLinks.push(link);
    else
      newInvisibleLinks.push(link);
  }

  visibleLinks.value = newVisibleLinks;
  invisibleLinks.value = newInvisibleLinks;
};

let resizeTimeoutId: ReturnType<typeof setTimeout> | null = null;
const debouncedResize = () => {
  if (resizeTimeoutId)
    clearTimeout(resizeTimeoutId);

  resizeTimeoutId = setTimeout(() => {
    calculateVisibleLinks();
  }, 150);
};

watch(() => [props.links, props.leftOffset, props.rightOffset] as const, () => {
  linkWidthCache.value.clear();
  calculateVisibleLinks();
}, { immediate: true, deep: true });

onMounted(() => {
  if (import.meta.client) {
    window.addEventListener("resize", debouncedResize);
    calculateVisibleLinks();
  }
});

onBeforeUnmount(() => {
  if (import.meta.client) {
    window.removeEventListener("resize", debouncedResize);

    if (resizeTimeoutId)
      clearTimeout(resizeTimeoutId);

    linkWidthCache.value.clear();
  }
});
</script>

<template>
  <div class="flex items-center">
    <BaseHeaderVisibleLinks :links="visibleLinks" />
    <BaseHeaderInvisibleLinks class="mt-2 hidden lg:block" :links="invisibleLinks" />
  </div>
</template>
